import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ColumnDef } from '@tanstack/react-table';
import { useForm, useFieldArray, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import toast from 'react-hot-toast';
import { adminProductsApi, adminCategoriesApi, Product, Category, CreateProductPayload } from '../services/adminApi';
import DataTable from './components/DataTable';
import Badge from './components/Badge';
import Modal from './components/Modal';

const ACCEPTED_IMAGE_TYPES = 'image/jpeg,image/png,image/webp,image/gif';

// Images have no upload pipeline in this project — they're static files under
// public/assets/, referenced by path. Accept that convention or a full URL.
const isValidImageRef = (v: string) => v.startsWith('/') || /^https?:\/\//i.test(v);

const productFormSchema = z
  .object({
    slug: z
      .string()
      .min(2, 'Trop court')
      .max(100)
      .regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/, 'Minuscules et tirets uniquement (ex: vanille-grade-a)'),
    name: z.string().min(2, 'Trop court').max(200),
    category: z.string().min(1, 'Catégorie requise'),
    grade: z.string().max(20).optional(),
    image: z
      .string()
      .min(1, 'Image requise')
      .refine(isValidImageRef, 'Chemin relatif (/assets/...) ou URL valide'),
    description_fr: z.string().min(10, 'Trop court (10 caractères min.)').max(5000),
    description_en: z.string().min(10, 'Trop court (10 caractères min.)').max(5000),
    in_stock: z.boolean(),
    min_order_kg: z.string().optional(),
    sort_order: z.number().int().optional(),
    specifications: z.array(
      z.object({
        name: z.string().min(1, 'Requis'),
        value: z.string().min(1, 'Requis'),
        unit: z.string().optional(),
      }),
    ),
    has_origin: z.boolean(),
    origin_region: z.string().optional(),
    origin_country: z.string().optional(),
    origin_altitude: z.string().optional(),
  })
  .refine((data) => !data.has_origin || !!data.origin_region?.trim(), {
    message: 'Région requise',
    path: ['origin_region'],
  })
  .refine((data) => !data.has_origin || !!data.origin_country?.trim(), {
    message: 'Pays requis',
    path: ['origin_country'],
  });

type ProductFormValues = z.infer<typeof productFormSchema>;

const emptyDefaults: ProductFormValues = {
  slug: '',
  name: '',
  category: '',
  grade: '',
  image: '',
  description_fr: '',
  description_en: '',
  in_stock: true,
  min_order_kg: '',
  sort_order: 0,
  specifications: [],
  has_origin: false,
  origin_region: '',
  origin_country: 'Madagascar',
  origin_altitude: '',
};

const productToFormValues = (p: Product): ProductFormValues => ({
  slug: p.slug,
  name: p.name,
  category: p.category as ProductFormValues['category'],
  grade: p.grade ?? '',
  image: p.image,
  description_fr: p.description_fr,
  description_en: p.description_en,
  in_stock: p.in_stock,
  min_order_kg: p.min_order_kg ?? '',
  sort_order: p.sort_order,
  specifications: p.specifications.map((s) => ({ name: s.name, value: s.value, unit: s.unit ?? '' })),
  has_origin: !!p.origin,
  origin_region: p.origin?.region ?? '',
  origin_country: p.origin?.country ?? 'Madagascar',
  origin_altitude: p.origin?.altitude ?? '',
});

const inputClass =
  'w-full px-3 py-2 border border-cream-200 rounded-lg text-sm bg-cream-50 text-coffee-200 outline-none focus:ring-2 focus:ring-forest-200 focus:border-transparent transition-all';
const labelClass = 'block text-sm font-medium text-coffee-200 mb-1';
const errorClass = 'mt-1 text-xs text-red-600';

// ── Small reusable "tag list" input (used for gallery images & certifications) ──
const TagListEditor: React.FC<{
  label: string;
  placeholder: string;
  values: string[];
  onChange: (values: string[]) => void;
}> = ({ label, placeholder, values, onChange }) => {
  const [draft, setDraft] = useState('');

  const add = () => {
    const v = draft.trim();
    if (!v) return;
    onChange([...values, v]);
    setDraft('');
  };

  return (
    <div>
      <label className={labelClass}>{label}</label>
      <div className="flex gap-2">
        <input
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') { e.preventDefault(); add(); }
          }}
          placeholder={placeholder}
          className={inputClass}
        />
        <button type="button" onClick={add} className="shrink-0 px-4 py-2 bg-cream-200 text-coffee-200 rounded-lg text-sm font-medium hover:bg-cream-300 transition-colors">
          Ajouter
        </button>
      </div>
      {values.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-2">
          {values.map((v, i) => (
            <span key={`${v}-${i}`} className="inline-flex items-center gap-1.5 bg-forest-200/10 text-forest-200 text-xs font-medium px-2.5 py-1 rounded-full">
              {v}
              <button type="button" onClick={() => onChange(values.filter((_, idx) => idx !== i))} className="hover:text-red-600" aria-label={`Retirer ${v}`}>
                ×
              </button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
};

// ── Create / edit form modal ──
const ProductFormModal: React.FC<{
  product: Product | null;
  categories: Category[];
  onClose: () => void;
  onSaved: () => void;
  onUnauthorized: () => void;
}> = ({ product, categories, onClose, onSaved, onUnauthorized }) => {
  const isEdit = !!product;
  const [images, setImages] = useState<string[]>(product?.images ?? []);
  const [certifications, setCertifications] = useState<string[]>(product?.certifications ?? []);
  const [submitting, setSubmitting] = useState(false);

  const {
    register,
    control,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<ProductFormValues>({
    resolver: zodResolver(productFormSchema),
    defaultValues: product ? productToFormValues(product) : emptyDefaults,
  });

  const { fields, append, remove } = useFieldArray({ control, name: 'specifications' });
  const hasOrigin = watch('has_origin');

  const onSubmit = async (values: ProductFormValues) => {
    setSubmitting(true);
    const payload: CreateProductPayload = {
      slug: values.slug,
      name: values.name,
      category: values.category,
      grade: values.grade?.trim() ? values.grade.trim() : null,
      image: values.image.trim(),
      images,
      description_fr: values.description_fr,
      description_en: values.description_en,
      certifications,
      in_stock: values.in_stock,
      min_order_kg: values.min_order_kg?.trim() ? values.min_order_kg.trim() : null,
      sort_order: values.sort_order ?? 0,
      specifications: values.specifications.map((s) => ({
        name: s.name.trim(),
        value: s.value.trim(),
        unit: s.unit?.trim() ? s.unit.trim() : null,
      })),
      origin: values.has_origin
        ? {
            region: (values.origin_region ?? '').trim(),
            country: (values.origin_country ?? 'Madagascar').trim(),
            altitude: values.origin_altitude?.trim() ? values.origin_altitude.trim() : null,
          }
        : null,
    };

    try {
      if (isEdit) {
        await adminProductsApi.update(product.id, payload);
        toast.success('Produit mis à jour');
      } else {
        await adminProductsApi.create(payload);
        toast.success('Produit créé');
      }
      onSaved();
    } catch (e: any) {
      if (e.message === 'UNAUTHORIZED') { onUnauthorized(); return; }
      toast.error(e.message === 'Slug already exists' ? 'Ce slug existe déjà' : (e.message || "Erreur lors de l'enregistrement"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal onClose={onClose} maxWidth="max-w-3xl">
      <div className="flex justify-between items-start mb-5">
        <h2 className="font-display text-xl font-bold text-coffee-200">
          {isEdit ? `Modifier — ${product!.name}` : 'Nouveau produit'}
        </h2>
        <button onClick={onClose} className="text-coffee-100 hover:text-coffee-200 text-2xl leading-none" aria-label="Fermer">×</button>
      </div>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className={labelClass}>Nom *</label>
              <input {...register('name')} className={inputClass} placeholder="Vanille Grade A Gourmet" />
              {errors.name && <p className={errorClass}>{errors.name.message}</p>}
            </div>
            <div>
              <label className={labelClass}>Slug *</label>
              <input {...register('slug')} className={inputClass} placeholder="vanille-grade-a-gourmet" disabled={isEdit} />
              {errors.slug && <p className={errorClass}>{errors.slug.message}</p>}
              {isEdit && <p className="mt-1 text-xs text-coffee-100">Le slug ne peut pas être modifié après création.</p>}
            </div>
            <div>
              <label className={labelClass}>Catégorie *</label>
              <select {...register('category')} className={inputClass}>
                <option value="" disabled>Choisir une catégorie...</option>
                {categories.map((c) => <option key={c.id} value={c.id}>{c.label_fr}</option>)}
              </select>
              {errors.category && <p className={errorClass}>{errors.category.message}</p>}
            </div>
            <div>
              <label className={labelClass}>Grade (optionnel)</label>
              <input {...register('grade')} className={inputClass} placeholder="A, B, 1, Extra..." />
            </div>
          </div>

          <div>
            <label className={labelClass}>Image principale *</label>
            <Controller
              control={control}
              name="image"
              render={({ field }) => (
                <ImageUploadField value={field.value} onChange={field.onChange} onUnauthorized={onUnauthorized} />
              )}
            />
            {errors.image && <p className={errorClass}>{errors.image.message}</p>}
          </div>

          <ImageGalleryEditor label="Images additionnelles (galerie)" values={images} onChange={setImages} onUnauthorized={onUnauthorized} />

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className={labelClass}>Description (FR) *</label>
              <textarea {...register('description_fr')} rows={4} className={`${inputClass} resize-none`} />
              {errors.description_fr && <p className={errorClass}>{errors.description_fr.message}</p>}
            </div>
            <div>
              <label className={labelClass}>Description (EN) *</label>
              <textarea {...register('description_en')} rows={4} className={`${inputClass} resize-none`} />
              {errors.description_en && <p className={errorClass}>{errors.description_en.message}</p>}
            </div>
          </div>

          {/* Specifications */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className={labelClass + ' mb-0'}>Spécifications techniques</label>
              <button type="button" onClick={() => append({ name: '', value: '', unit: '' })} className="text-xs font-medium text-forest-200 hover:text-forest-100">
                + Ajouter une ligne
              </button>
            </div>
            {fields.length === 0 && <p className="text-xs text-coffee-100 italic">Aucune spécification.</p>}
            <div className="space-y-2">
              {fields.map((field, index) => (
                <div key={field.id} className="grid grid-cols-[1fr_1fr_80px_auto] gap-2 items-start">
                  <div>
                    <input {...register(`specifications.${index}.name` as const)} placeholder="Nom (ex: Vanilline)" className={inputClass} />
                    {errors.specifications?.[index]?.name && <p className={errorClass}>{errors.specifications[index]?.name?.message}</p>}
                  </div>
                  <div>
                    <input {...register(`specifications.${index}.value` as const)} placeholder="Valeur (ex: >1.8)" className={inputClass} />
                    {errors.specifications?.[index]?.value && <p className={errorClass}>{errors.specifications[index]?.value?.message}</p>}
                  </div>
                  <input {...register(`specifications.${index}.unit` as const)} placeholder="Unité" className={inputClass} />
                  <button type="button" onClick={() => remove(index)} className="px-2 py-2 text-red-500 hover:text-red-700" aria-label="Supprimer la ligne">
                    ×
                  </button>
                </div>
              ))}
            </div>
          </div>

          <TagListEditor label="Certifications" placeholder="Bio, Fair Trade..." values={certifications} onChange={setCertifications} />

          {/* Origin */}
          <div className="border border-cream-200 rounded-lg p-4">
            <label className="flex items-center gap-2 text-sm font-medium text-coffee-200 cursor-pointer">
              <input type="checkbox" {...register('has_origin')} className="rounded border-cream-200 text-forest-200 focus:ring-forest-200" />
              Renseigner l'origine géographique
            </label>
            {hasOrigin && (
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-3">
                <div>
                  <label className={labelClass}>Région</label>
                  <input {...register('origin_region')} className={inputClass} placeholder="SAVA" />
                  {errors.origin_region && <p className={errorClass}>{errors.origin_region.message}</p>}
                </div>
                <div>
                  <label className={labelClass}>Pays</label>
                  <input {...register('origin_country')} className={inputClass} />
                  {errors.origin_country && <p className={errorClass}>{errors.origin_country.message}</p>}
                </div>
                <div>
                  <label className={labelClass}>Altitude (optionnel)</label>
                  <input {...register('origin_altitude')} className={inputClass} placeholder="800-1200m" />
                </div>
              </div>
            )}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <label className="flex items-center gap-2 text-sm font-medium text-coffee-200 cursor-pointer">
              <input type="checkbox" {...register('in_stock')} className="rounded border-cream-200 text-forest-200 focus:ring-forest-200" />
              En stock
            </label>
            <div>
              <label className={labelClass}>Quantité min. (kg, optionnel)</label>
              <input type="number" step="0.01" min="0" {...register('min_order_kg')} className={inputClass} />
            </div>
            <div>
              <label className={labelClass}>Ordre d'affichage</label>
              <input type="number" step="1" {...register('sort_order', { valueAsNumber: true })} className={inputClass} />
            </div>
          </div>

          <div className="flex gap-3 pt-2 border-t border-cream-200">
            <button type="button" onClick={onClose} className="flex-1 py-2.5 border border-cream-200 rounded-lg text-sm text-coffee-100 hover:bg-cream-100 transition-colors">
              Annuler
            </button>
            <button type="submit" disabled={submitting} className="flex-1 py-2.5 bg-forest-200 text-cream-50 rounded-lg text-sm font-semibold hover:bg-forest-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors">
              {submitting ? 'Enregistrement...' : isEdit ? 'Enregistrer les modifications' : 'Créer le produit'}
            </button>
          </div>
        </form>
    </Modal>
  );
};

// Tiny live thumbnail preview next to the image-path input.
const ImagePreview: React.FC<{ src?: string; className?: string }> = ({ src, className }) => {
  if (!src) return <div className={className ?? 'w-14 h-14 shrink-0 rounded-lg bg-cream-200'} />;
  return (
    <img
      src={src}
      alt=""
      className={className ?? 'w-14 h-14 shrink-0 rounded-lg object-cover bg-cream-200 border border-cream-200'}
      onError={(e) => { (e.target as HTMLImageElement).style.visibility = 'hidden'; }}
      onLoad={(e) => { (e.target as HTMLImageElement).style.visibility = 'visible'; }}
    />
  );
};

// ── Cover image: upload, preview, replace/remove ──
const ImageUploadField: React.FC<{
  value: string;
  onChange: (path: string) => void;
  onUnauthorized: () => void;
}> = ({ value, onChange, onUnauthorized }) => {
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const handleFile = async (file: File) => {
    setUploading(true);
    try {
      const { path } = await adminProductsApi.uploadImage(file);
      onChange(path);
    } catch (e: any) {
      if (e.message === 'UNAUTHORIZED') { onUnauthorized(); return; }
      toast.error(e.message || "Erreur lors de l'envoi de l'image");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="flex gap-3 items-start">
      <ImagePreview src={value} />
      <div className="flex-1 min-w-0 space-y-1.5">
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            disabled={uploading}
            className="px-4 py-2 bg-cream-200 text-coffee-200 rounded-lg text-sm font-medium hover:bg-cream-300 disabled:opacity-50 transition-colors"
          >
            {uploading ? 'Envoi...' : value ? "Changer l'image" : 'Choisir une image'}
          </button>
          {value && !uploading && (
            <button type="button" onClick={() => onChange('')} className="px-3 py-2 text-red-500 hover:text-red-700 text-sm">
              Retirer
            </button>
          )}
        </div>
        <input
          ref={inputRef}
          type="file"
          accept={ACCEPTED_IMAGE_TYPES}
          className="hidden"
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) handleFile(file);
            e.target.value = '';
          }}
        />
        <p className="text-xs text-coffee-100 truncate">{value || 'Aucune image sélectionnée'}</p>
      </div>
    </div>
  );
};

// ── Gallery: thumbnail grid with an upload tile, used for additional product images ──
const ImageGalleryEditor: React.FC<{
  label: string;
  values: string[];
  onChange: (values: string[]) => void;
  onUnauthorized: () => void;
}> = ({ label, values, onChange, onUnauthorized }) => {
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const handleFile = async (file: File) => {
    setUploading(true);
    try {
      const { path } = await adminProductsApi.uploadImage(file);
      onChange([...values, path]);
    } catch (e: any) {
      if (e.message === 'UNAUTHORIZED') { onUnauthorized(); return; }
      toast.error(e.message || "Erreur lors de l'envoi de l'image");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div>
      <label className={labelClass}>{label}</label>
      <div className="flex flex-wrap gap-3">
        {values.map((v, i) => (
          <div key={`${v}-${i}`} className="relative">
            <ImagePreview src={v} className="w-16 h-16 rounded-lg object-cover bg-cream-200 border border-cream-200" />
            <button
              type="button"
              onClick={() => onChange(values.filter((_, idx) => idx !== i))}
              className="absolute -top-2 -right-2 w-5 h-5 bg-red-500 text-white rounded-full text-xs flex items-center justify-center hover:bg-red-600"
              aria-label="Retirer cette image"
            >
              ×
            </button>
          </div>
        ))}
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          disabled={uploading}
          className="w-16 h-16 shrink-0 rounded-lg border-2 border-dashed border-cream-200 text-coffee-100 hover:border-forest-200 hover:text-forest-200 flex items-center justify-center text-xs font-medium transition-colors disabled:opacity-50"
        >
          {uploading ? '...' : '+ Ajouter'}
        </button>
      </div>
      <input
        ref={inputRef}
        type="file"
        accept={ACCEPTED_IMAGE_TYPES}
        className="hidden"
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) handleFile(file);
          e.target.value = '';
        }}
      />
    </div>
  );
};

// ── Read-only detail view: full-size gallery + every field on the product ──
const ProductDetailModal: React.FC<{
  product: Product;
  categoryLabel: string;
  onClose: () => void;
}> = ({ product, categoryLabel, onClose }) => {
  const images = useMemo(
    () => [product.image, ...product.images].filter((v, i, arr) => !!v && arr.indexOf(v) === i),
    [product.image, product.images],
  );
  const [activeIndex, setActiveIndex] = useState(0);
  const activeImage = images[activeIndex] ?? images[0];

  return (
    <Modal onClose={onClose} maxWidth="max-w-3xl">
      <div className="flex justify-between items-start mb-5">
        <div>
          <h2 className="font-display text-xl font-bold text-coffee-200">{product.name}</h2>
          <p className="text-xs text-coffee-100">{product.slug}</p>
        </div>
        <button onClick={onClose} className="text-coffee-100 hover:text-coffee-200 text-2xl leading-none" aria-label="Fermer">×</button>
      </div>

      {/* Gallery */}
      <div className="mb-6">
        <div className="relative aspect-[4/3] w-full overflow-hidden rounded-xl bg-cream-200">
          {activeImage ? (
            <img src={activeImage} alt={product.name} className="h-full w-full object-cover" />
          ) : (
            <div className="flex h-full items-center justify-center text-sm text-coffee-100">Aucune image</div>
          )}
          {images.length > 1 && (
            <>
              <button
                type="button"
                onClick={() => setActiveIndex((i) => (i - 1 + images.length) % images.length)}
                className="absolute left-2 top-1/2 -translate-y-1/2 flex h-8 w-8 items-center justify-center rounded-full bg-white/80 text-coffee-200 shadow hover:bg-white transition-colors"
                aria-label="Image précédente"
              >
                ‹
              </button>
              <button
                type="button"
                onClick={() => setActiveIndex((i) => (i + 1) % images.length)}
                className="absolute right-2 top-1/2 -translate-y-1/2 flex h-8 w-8 items-center justify-center rounded-full bg-white/80 text-coffee-200 shadow hover:bg-white transition-colors"
                aria-label="Image suivante"
              >
                ›
              </button>
            </>
          )}
        </div>
        {images.length > 1 && (
          <div className="mt-3 flex flex-wrap gap-2">
            {images.map((img, i) => (
              <button
                key={`${img}-${i}`}
                type="button"
                onClick={() => setActiveIndex(i)}
                className={`h-14 w-14 shrink-0 overflow-hidden rounded-lg border-2 transition-colors ${
                  i === activeIndex ? 'border-forest-200' : 'border-transparent hover:border-cream-200'
                }`}
                aria-label={`Voir l'image ${i + 1}`}
              >
                <img src={img} alt="" className="h-full w-full object-cover" />
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Key facts */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
        <div className="rounded-xl bg-cream-50 border border-cream-200 p-3">
          <div className="text-xs text-coffee-100">Catégorie</div>
          <div className="text-sm font-medium text-coffee-200">{categoryLabel}</div>
        </div>
        <div className="rounded-xl bg-cream-50 border border-cream-200 p-3">
          <div className="text-xs text-coffee-100">Grade</div>
          <div className="text-sm font-medium text-coffee-200">{product.grade || '—'}</div>
        </div>
        <div className="rounded-xl bg-cream-50 border border-cream-200 p-3">
          <div className="text-xs text-coffee-100 mb-1">Stock</div>
          <Badge tone={product.in_stock ? 'green' : 'red'}>{product.in_stock ? 'En stock' : 'Rupture'}</Badge>
        </div>
        <div className="rounded-xl bg-cream-50 border border-cream-200 p-3">
          <div className="text-xs text-coffee-100">Qté min.</div>
          <div className="text-sm font-medium text-coffee-200">{product.min_order_kg ? `${product.min_order_kg} kg` : '—'}</div>
        </div>
      </div>

      {/* Descriptions */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
        <div>
          <h3 className="text-xs font-bold uppercase tracking-wide text-coffee-100 mb-1.5">Description (FR)</h3>
          <p className="text-sm text-coffee-200 leading-relaxed whitespace-pre-wrap">{product.description_fr}</p>
        </div>
        <div>
          <h3 className="text-xs font-bold uppercase tracking-wide text-coffee-100 mb-1.5">Description (EN)</h3>
          <p className="text-sm text-coffee-200 leading-relaxed whitespace-pre-wrap">{product.description_en}</p>
        </div>
      </div>

      {/* Specifications */}
      {product.specifications.length > 0 && (
        <div className="mb-6">
          <h3 className="text-xs font-bold uppercase tracking-wide text-coffee-100 mb-2">Spécifications techniques</h3>
          <div className="overflow-hidden rounded-xl border border-cream-200">
            <table className="w-full text-sm">
              <tbody>
                {product.specifications.map((spec, i) => (
                  <tr key={i} className={i % 2 === 0 ? 'bg-cream-50' : 'bg-white'}>
                    <td className="px-3 py-2 font-medium text-coffee-200">{spec.name}</td>
                    <td className="px-3 py-2 text-coffee-100">{spec.value}{spec.unit ? ` ${spec.unit}` : ''}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Origin */}
      {product.origin && (
        <div className="mb-6">
          <h3 className="text-xs font-bold uppercase tracking-wide text-coffee-100 mb-2">Origine</h3>
          <p className="text-sm text-coffee-200">
            {product.origin.region}, {product.origin.country}
            {product.origin.altitude ? ` · Altitude ${product.origin.altitude}` : ''}
          </p>
        </div>
      )}

      {/* Certifications */}
      {product.certifications.length > 0 && (
        <div className="mb-2">
          <h3 className="text-xs font-bold uppercase tracking-wide text-coffee-100 mb-2">Certifications</h3>
          <div className="flex flex-wrap gap-2">
            {product.certifications.map((c) => <Badge key={c} tone="blue">{c}</Badge>)}
          </div>
        </div>
      )}

      <div className="flex gap-3 pt-4 border-t border-cream-200 mt-2">
        <button onClick={onClose} className="flex-1 py-2.5 border border-cream-200 rounded-lg text-sm text-coffee-100 hover:bg-cream-100 transition-colors">
          Fermer
        </button>
      </div>
    </Modal>
  );
};

// ── Delete confirmation modal ──
const DeleteConfirmModal: React.FC<{
  product: Product;
  onClose: () => void;
  onDeleted: () => void;
  onUnauthorized: () => void;
}> = ({ product, onClose, onDeleted, onUnauthorized }) => {
  const [deleting, setDeleting] = useState(false);

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await adminProductsApi.delete(product.id);
      toast.success('Produit supprimé');
      onDeleted();
    } catch (e: any) {
      if (e.message === 'UNAUTHORIZED') { onUnauthorized(); return; }
      toast.error(e.message || 'Erreur lors de la suppression');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Modal onClose={onClose} maxWidth="max-w-sm">
      <h2 className="font-display text-lg font-bold text-coffee-200 mb-2">Supprimer ce produit ?</h2>
      <p className="text-sm text-coffee-100 mb-5">
        <span className="font-medium text-coffee-200">{product.name}</span> sera définitivement supprimé. Cette action est irréversible.
      </p>
      <div className="flex gap-3">
        <button onClick={onClose} className="flex-1 py-2.5 border border-cream-200 rounded-lg text-sm text-coffee-100 hover:bg-cream-100 transition-colors">
          Annuler
        </button>
        <button onClick={handleDelete} disabled={deleting} className="flex-1 py-2.5 bg-red-500 text-white rounded-lg text-sm font-semibold hover:bg-red-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors">
          {deleting ? 'Suppression...' : 'Supprimer'}
        </button>
      </div>
    </Modal>
  );
};

// ── Main tab component ──
interface Props {
  onLogout: () => void;
  onCountChange?: (count: number) => void;
}

const AdminProducts: React.FC<Props> = ({ onLogout, onCountChange }) => {
  const [products, setProducts] = useState<Product[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [categoryFilter, setCategoryFilter] = useState('');
  const [formTarget, setFormTarget] = useState<'new' | Product | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Product | null>(null);
  const [detailTarget, setDetailTarget] = useState<Product | null>(null);

  const categoryLabel = useCallback(
    (id: string) => categories.find((c) => c.id === id)?.label_fr ?? id,
    [categories],
  );

  const fetchAll = useCallback(async () => {
    try {
      const [productsData, categoriesData] = await Promise.all([
        adminProductsApi.getAll(),
        adminCategoriesApi.getAll(),
      ]);
      const sorted = [...productsData].sort((a, b) => a.sort_order - b.sort_order || a.name.localeCompare(b.name));
      setProducts(sorted);
      setCategories(categoriesData);
      onCountChange?.(sorted.length);
    } catch (e: any) {
      if (e.message === 'UNAUTHORIZED') { onLogout(); }
      else { toast.error('Impossible de charger les produits'); }
    } finally {
      setLoading(false);
    }
  }, [onLogout, onCountChange]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const filtered = useMemo(() => {
    if (!categoryFilter) return products;
    return products.filter((p) => p.category === categoryFilter);
  }, [products, categoryFilter]);

  const columns = useMemo<ColumnDef<Product, any>[]>(() => [
    {
      accessorKey: 'name',
      header: 'Produit',
      cell: (info) => (
        <div className="flex items-center gap-3">
          <img
            src={info.row.original.image}
            alt=""
            className="h-10 w-10 shrink-0 rounded-lg bg-cream-200 object-cover"
            onError={(e) => { (e.target as HTMLImageElement).style.visibility = 'hidden'; }}
          />
          <div className="min-w-0">
            <div className="truncate font-medium text-coffee-200">{info.getValue() as string}</div>
            <div className="truncate text-xs text-coffee-100">{info.row.original.slug}</div>
          </div>
        </div>
      ),
    },
    {
      accessorKey: 'category',
      header: 'Catégorie',
      cell: (info) => <span className="text-coffee-100">{categoryLabel(info.getValue() as string)}</span>,
    },
    { accessorKey: 'grade', header: 'Grade', cell: (info) => <span className="text-coffee-100">{(info.getValue() as string) || '—'}</span> },
    {
      accessorKey: 'in_stock',
      header: 'Stock',
      cell: (info) => <Badge tone={info.getValue() ? 'green' : 'red'}>{info.getValue() ? 'En stock' : 'Rupture'}</Badge>,
    },
    { accessorKey: 'sort_order', header: 'Ordre', cell: (info) => <span className="text-coffee-100">{info.getValue() as number}</span> },
    {
      id: 'actions',
      header: '',
      enableSorting: false,
      cell: (info) => (
        <div className="flex gap-2">
          <button onClick={() => setDetailTarget(info.row.original)} className="rounded-lg border border-cream-200 px-3 py-1.5 text-xs font-semibold text-coffee-200 transition-colors hover:bg-cream-100">
            Voir détails
          </button>
          <button onClick={() => setFormTarget(info.row.original)} className="rounded-lg bg-forest-200 px-3 py-1.5 text-xs font-semibold text-cream-50 transition-colors hover:bg-forest-100">
            Modifier
          </button>
          <button onClick={() => setDeleteTarget(info.row.original)} className="rounded-lg border border-red-300 px-3 py-1.5 text-xs font-semibold text-red-600 transition-colors hover:bg-red-50">
            Supprimer
          </button>
        </div>
      ),
    },
  ], [categoryLabel]);

  return (
    <div>
      <DataTable
        columns={columns}
        data={filtered}
        loading={loading}
        emptyMessage="Aucun produit"
        searchPlaceholder="Rechercher un produit..."
        toolbarExtra={
          <>
            <select
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
              className="rounded-xl border border-cream-200 bg-white px-3 py-2 text-sm text-coffee-200 shadow-sm outline-none transition-all focus:border-forest-200 focus:ring-2 focus:ring-forest-200/30"
            >
              <option value="">Toutes catégories</option>
              {categories.map((c) => <option key={c.id} value={c.id}>{c.label_fr}</option>)}
            </select>
            <button
              onClick={() => setFormTarget('new')}
              className="rounded-full bg-forest-200 px-5 py-2 text-sm font-medium text-cream-50 transition-colors hover:bg-forest-100"
            >
              + Ajouter un produit
            </button>
          </>
        }
      />

      {detailTarget && (
        <ProductDetailModal
          product={detailTarget}
          categoryLabel={categoryLabel(detailTarget.category)}
          onClose={() => setDetailTarget(null)}
        />
      )}

      {formTarget && (
        <ProductFormModal
          product={formTarget === 'new' ? null : formTarget}
          categories={categories}
          onClose={() => setFormTarget(null)}
          onSaved={() => { setFormTarget(null); fetchAll(); }}
          onUnauthorized={onLogout}
        />
      )}

      {deleteTarget && (
        <DeleteConfirmModal
          product={deleteTarget}
          onClose={() => setDeleteTarget(null)}
          onDeleted={() => { setDeleteTarget(null); fetchAll(); }}
          onUnauthorized={onLogout}
        />
      )}
    </div>
  );
};

export default AdminProducts;
